import { api } from './api';

export type FleetAssignmentPayload = {
  driverId: string;
  vehicleId: string;
  routeId?: string;
};

export type FleetManagerKycPayload = {
  operatorType: 'individual' | 'company';
  fullName: string;
  phoneNumber: string;
  nationalId: string;
  companyName?: string | null;
  companyRegNumber?: string | null;
};

export type FleetKycStatusResponse = {
  status?: string;
  kyc_status?: string;
  onboarding_status?: string;
  submitted_at?: string | null;
  reviewed_at?: string | null;
  rejection_reason?: string | null;
  [key: string]: unknown;
};

export type VehiclePayload = {
  id?: string;
  fleet_id?: string | null;
  registration_number: string;
  make: string;
  model: string;
  year?: number | null;
  seating_capacity: number;
  type?: string;
  color?: string | null;
  odometer?: number | null;
  maintenance_date?: string | null;
  tracker_device_id?: string | null;
  metadata?: Record<string, unknown>;
  status?: string;
};

export type FleetDashboardMetric = {
  value?: number;
  delta?: number;
};

export type FleetDashboardActivity = {
  day?: string;
  label?: string;
  date?: string;
  value?: number;
  count?: number;
};

export type FleetDashboardAlert = {
  id?: string;
  title?: string;
  message?: string;
  body?: string;
  type?: string;
  tone?: string;
};

export type FleetDashboardAssignment = {
  id?: string;
  route?: string;
  route_name?: string;
  driver?: string;
  driver_name?: string;
  vehicle?: string;
  vehicle_registration_number?: string;
  vehicle_plate_number?: string;
  schedule?: string;
  status?: string;
};

export type FleetDashboardResponse = {
  stats?: {
    activeVehicles?: FleetDashboardMetric;
    active_vehicles?: FleetDashboardMetric;
    onDutyDrivers?: FleetDashboardMetric;
    on_duty_drivers?: FleetDashboardMetric;
    activeRoutes?: FleetDashboardMetric;
    active_routes?: FleetDashboardMetric;
    openAlerts?: FleetDashboardMetric;
    open_alerts?: FleetDashboardMetric;
  };
  active_vehicles_total?: number;
  on_duty_drivers_total?: number;
  active_routes_total?: number;
  open_alerts_total?: number;
  activity?: FleetDashboardActivity[];
  fleet_activity?: FleetDashboardActivity[];
  alerts?: FleetDashboardAlert[];
  active_alerts?: FleetDashboardAlert[];
  recentAssignments?: FleetDashboardAssignment[];
  recent_assignments?: FleetDashboardAssignment[];
};

export type FleetMapVehicle = {
  id?: string;
  vehicle_id?: string;
  name?: string;
  registration_number?: string;
  plateNumber?: string;
  plate_number?: string;
  route?: string;
  route_name?: string;
  route_code?: string;
  driver?: string;
  driver_name?: string;
  speed?: number;
  speed_kph?: number;
  status?: string;
  latitude?: number;
  longitude?: number;
  lat?: number;
  lng?: number;
  x?: number;
  y?: number;
  position?: {
    latitude?: number;
    longitude?: number;
    lat?: number;
    lng?: number;
    x?: number;
    y?: number;
  };
};

export type FleetLocationResponse = {
  vehicles?: FleetMapVehicle[];
  locations?: FleetMapVehicle[];
  history?: FleetMapVehicle[];
  liveVehicles?: FleetMapVehicle[];
  live_vehicles?: FleetMapVehicle[];
  items?: FleetMapVehicle[];
  results?: FleetMapVehicle[];
  data?: FleetMapVehicle[] | FleetLocationResponse;
};

export type OpsAuthorityResponse = {
  fleet_id?: string;
  fleetId?: string;
  fleet?: { id?: string; fleet_id?: string };
  authority?: Record<string, unknown>[] | { fleet_id?: string; fleetId?: string; fleet?: { id?: string; fleet_id?: string } };
  fleets?: Record<string, unknown>[];
  groups?: Record<string, unknown>[];
  items?: Record<string, unknown>[];
  results?: Record<string, unknown>[];
  data?: OpsAuthorityResponse;
};

export type OpsAssignment = Record<string, unknown>;
export type OpsDriver = Record<string, unknown>;
export type OpsDriverFilters = {
  fleet_id?: string;
  status?: string;
};
export type OpsListResponse<T> = T[] | {
  items?: T[];
  results?: T[];
  assignments?: T[];
  drivers?: T[];
  data?: T[] | OpsListResponse<T>;
};

export type CreateFleetPayload = {
  name: string;
  description?: string;
  parent_id?: string | null;
  group_type: 'fleet' | 'authority';
};

export type UpdateFleetPayload = {
  name?: string;
  description?: string;
  parent_id?: string | null;
};

export type SubFleetResponse = {
  id: string;
  fleet_manager_id: string;
  fleet_id: string;
  name: string;
  description: string | null;
  driver_ids: string[];
  vehicle_ids: string[];
  status: 'active' | 'inactive' | string;
  created_at: string;
  updated_at: string;
};

export type SubFleetPayload = {
  name: string;
  fleet_id: string;
  description?: string;
  driver_ids?: string[];
  vehicle_ids?: string[];
};

export type SubFleetListParams = {
  search?: string;
  fleet_id?: string;
  status?: 'active' | 'inactive';
  limit?: number;
  offset?: number;
};

export type SubFleetListResponse = {
  subfleets: SubFleetResponse[];
  total: number;
  limit: number;
  offset: number;
};

export function getFleetDashboard() {
  return api.get<FleetDashboardResponse>('/fleet/dashboard');
}

export function submitFleetManagerKyc(payload: FleetManagerKycPayload) {
  return registerFleetOperator(payload)
    .then(() => submitOnboardingKyc())
    .then(() => getOnboardingKycStatus());
}

export function registerFleetOperator(payload: FleetManagerKycPayload) {
  return api.post<Record<string, unknown>>('/api/v1/onboarding/fleet-operator/register', {
    operator_type: payload.operatorType,
    full_name: payload.fullName,
    company_name: payload.operatorType === 'company' ? payload.companyName : null,
    company_reg_number: payload.operatorType === 'company' ? payload.companyRegNumber : null,
    phone_number: payload.phoneNumber,
    national_id: payload.nationalId,
  });
}

export function submitOnboardingKyc() {
  return api.post<Record<string, unknown>>('/api/v1/onboarding/kyc/submit');
}

export function getOnboardingKycStatus() {
  return api.get<FleetKycStatusResponse>('/api/v1/onboarding/kyc/status');
}

export function getOpsAuthority() {
  return api.get<OpsAuthorityResponse>('/ops/authority');
}

export function getFleetLocations(fleetId: string) {
  return api.get<FleetLocationResponse | FleetMapVehicle[]>(`/location/fleet/${encodeURIComponent(fleetId)}`);
}

export function getVehicleLocationHistory(vehicleId: string, minutes = 60) {
  return api.get<FleetLocationResponse | FleetMapVehicle[]>(
    `/location/vehicle/${encodeURIComponent(vehicleId)}/history?minutes=${minutes}`,
  );
}

export function getOpsAssignments(fleetId: string) {
  return api.get<OpsListResponse<OpsAssignment>>(
    `/ops/assignments?fleet_id=${encodeURIComponent(fleetId)}&status=in_progress`,
  );
}

export function getOpsDrivers(fleetId: string) {
  return getDrivers({ fleet_id: fleetId, status: 'active' });
}

export type AssignmentCreatePayload = {
  driver_id: string;
  vehicle_id: string;
  start_at: string;
  end_at: string;
  fleet_id?: string | null;
  sub_fleet_id?: string | null;
  route_catalog_id?: string | null;
  route_id?: string | null;
  route_name?: string | null;
  notes?: string | null;
};

export type AssignmentOut = {
  id: string;
  fleet_manager_id: string;
  fleet_id: string | null;
  sub_fleet_id: string | null;
  driver_id: string;
  vehicle_id: string;
  route_catalog_id: string | null;
  route_id: string | null;
  route_name: string | null;
  start_at: string;
  end_at: string;
  status: string;
  notes: string | null;
  completed_at: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type AssignmentFilters = {
  fleet_id?: string;
  sub_fleet_id?: string;
  status?: string;
};

export type RouteOption = { id: string; route_code: string; name: string; origin_name: string; destination_name: string; };

export function getAssignments(filters: AssignmentFilters = {}) {
  const q = new URLSearchParams();
  if (filters.fleet_id) q.set('fleet_id', filters.fleet_id);
  if (filters.sub_fleet_id) q.set('sub_fleet_id', filters.sub_fleet_id);
  if (filters.status) q.set('status', filters.status);
  const suffix = q.toString();
  return api.get<{ assignments: AssignmentOut[] }>(`/assignments${suffix ? `?${suffix}` : ''}`);
}

export function getAssignment(assignmentId: string) {
  return api.get<AssignmentOut>(`/assignments/${encodeURIComponent(assignmentId)}`);
}

export function createAssignment(payload: AssignmentCreatePayload) {
  return api.post<AssignmentOut>('/assignments', payload);
}

export function completeAssignment(assignmentId: string) {
  return api.post<AssignmentOut>(`/assignments/${encodeURIComponent(assignmentId)}/complete`);
}

export function getRoutes() {
  return api.get<{ routes: RouteOption[] } | RouteOption[]>('/routes');
}

export function getDrivers(filters: OpsDriverFilters = {}) {
  const query = new URLSearchParams();
  if (filters.fleet_id) query.set('fleet_id', filters.fleet_id);
  if (filters.status) query.set('status', filters.status);
  const suffix = query.toString();
  return api.get<OpsListResponse<OpsDriver>>(`/ops/drivers${suffix ? `?${suffix}` : ''}`);
}

export function createDriver(payload: Record<string, unknown>) {
  return api.post<Record<string, unknown>>('/ops/drivers', payload);
}

export function updateDriver(driverId: string, payload: Record<string, unknown>) {
  return api.patch<Record<string, unknown>>(`/ops/drivers/${encodeURIComponent(driverId)}`, payload);
}

export function deactivateDriver(driverId: string) {
  return api.post<Record<string, unknown>>(`/ops/drivers/${encodeURIComponent(driverId)}/deactivate`);
}

export function reactivateDriver(driverId: string) {
  return api.post<Record<string, unknown>>(`/ops/drivers/${encodeURIComponent(driverId)}/reactivate`);
}

export function inviteDriver(driverDailymoveId: string) {
  return api.post<Record<string, unknown>>('/ops/driver-invitations', {
    driver_dailymove_id: driverDailymoveId,
  });
}

export type FleetInvitationOut = {
  id: string;
  driver_id: string;
  fleet_id: string | null;
  fleet_manager_id: string;
  status: string;
  responded_at: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export function resolveFleetInvitation(token: string) {
  return api.get<FleetInvitationOut>(`/driver/fleet-invitations/resolve?token=${encodeURIComponent(token)}`);
}

export function acceptFleetInvitation(invitationId: string) {
  return api.post<FleetInvitationOut>(`/driver/fleet-invitations/${encodeURIComponent(invitationId)}/accept`);
}

export function rejectFleetInvitation(invitationId: string) {
  return api.post<FleetInvitationOut>(`/driver/fleet-invitations/${encodeURIComponent(invitationId)}/reject`);
}

export function assignDriverToFleet(driverId: string, fleetId: string) {
  return api.post<Record<string, unknown>>(`/ops/drivers/${encodeURIComponent(driverId)}/fleets`, {
    fleet_id: fleetId,
  });
}

export type VehicleFilters = {
  status?: string;
  type?: string;
  fleet_id?: string;
};

export function getVehicles(filters: VehicleFilters = {}) {
  const q = new URLSearchParams();
  if (filters.status) q.set('status', filters.status);
  if (filters.type) q.set('type', filters.type);
  if (filters.fleet_id) q.set('fleet_id', filters.fleet_id);
  const suffix = q.toString();
  return api.get(`/vehicles${suffix ? `?${suffix}` : ''}`);
}

export function createVehicle(payload: VehiclePayload) {
  return api.post('/vehicles', payload);
}

export function updateVehicle(vehicleId: string, payload: Partial<VehiclePayload>) {
  return api.patch(`/vehicles/${encodeURIComponent(vehicleId)}`, payload);
}

export function deactivateVehicle(vehicleId: string) {
  return api.delete<Record<string, unknown>>(`/vehicles/${encodeURIComponent(vehicleId)}`);
}

export function activateVehicle(vehicleId: string) {
  return api.post<Record<string, unknown>>(`/vehicles/${encodeURIComponent(vehicleId)}/activate`);
}

export function createFleet(payload: CreateFleetPayload) {
  return api.post<Record<string, unknown>>('/ops/fleet', payload);
}

export function getFleetGroup(groupId: string) {
  return api.get<Record<string, unknown>>(`/ops/fleet/${encodeURIComponent(groupId)}`);
}

export function updateFleetGroup(groupId: string, payload: UpdateFleetPayload) {
  return api.patch<Record<string, unknown>>(`/ops/fleet/${encodeURIComponent(groupId)}`, payload);
}

export function activateFleetGroup(groupId: string) {
  return api.post<Record<string, unknown>>(`/ops/fleet/${encodeURIComponent(groupId)}/activate`);
}

export function deactivateFleetGroup(groupId: string) {
  return api.post<Record<string, unknown>>(`/ops/fleet/${encodeURIComponent(groupId)}/deactivate`);
}

export function deleteFleetGroup(groupId: string) {
  return api.delete<void>(`/ops/fleet/${encodeURIComponent(groupId)}`);
}

export function getSubFleets(params: SubFleetListParams = {}) {
  const query = new URLSearchParams();
  if (params.search) query.set('search', params.search);
  if (params.fleet_id) query.set('fleet_id', params.fleet_id);
  if (params.status) query.set('status', params.status);
  query.set('limit', String(params.limit ?? 50));
  query.set('offset', String(params.offset ?? 0));
  return api.get<SubFleetListResponse>(`/subfleets?${query.toString()}`);
}

export function getSubFleet(subFleetId: string) {
  return api.get<SubFleetResponse>(`/subfleets/${encodeURIComponent(subFleetId)}`);
}

export function createSubFleet(payload: SubFleetPayload) {
  return api.post<SubFleetResponse>('/subfleets', payload);
}

export function updateSubFleet(subFleetId: string, payload: Partial<SubFleetPayload>) {
  return api.patch<SubFleetResponse>(`/subfleets/${encodeURIComponent(subFleetId)}`, payload);
}

export function activateSubFleet(subFleetId: string) {
  return api.post<SubFleetResponse>(`/subfleets/${encodeURIComponent(subFleetId)}/activate`);
}

export function deactivateSubFleet(subFleetId: string) {
  return api.post<SubFleetResponse>(`/subfleets/${encodeURIComponent(subFleetId)}/deactivate`);
}

export function deleteSubFleet(subFleetId: string) {
  return api.delete<SubFleetResponse>(`/subfleets/${encodeURIComponent(subFleetId)}`);
}
